'use client';

import { c as _c } from "react/compiler-runtime";
import type { Collection, UtilsRecord } from '@tanstack/db';
import { useLiveQuery } from '@tanstack/react-db';
import { useSyncExternalStore } from 'react';
const unsubscribeFromNothing = (): void => {};
const subscribeToNothing = (): (() => void) => unsubscribeFromNothing;
const isInBrowser = (): boolean => true;
const isOnServer = (): boolean => false;

/**
 * The schema bound `Collection` itself declares. `StandardSchemaAlias` is narrower: it requires
 * `'~standard'.types`, which Zod 4 types as optional, so a Zod-backed collection would not fit.
 */
type CollectionSchema = NonNullable<Collection['config']['schema']>;

/**
 * The rows of a collection kept in `localStorage`, live: none on the server and while the page
 * hydrates, the stored ones from the render right after.
 *
 * `useLiveQuery` hands React the browser's rows as its server snapshot too, so hydration saw
 * the rows of the last visit where the server had rendered none, failed, and React rendered the
 * whole page again. The server cannot read `localStorage`, so it is the browser that has to wait.
 */
export const useStoredRows = collection => {
  const $ = _c(5);
  let t0;
  if ($[0] !== collection) {
    t0 = {
      query: q => q.from({
        row: collection
      })
    };
    $[0] = collection;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    data
  } = useLiveQuery(t0);
  const isHydrated = useSyncExternalStore(subscribeToNothing, isInBrowser, isOnServer);
  let t1;
  if ($[2] !== data || $[3] !== isHydrated) {
    t1 = isHydrated ? data : [];
    $[2] = data;
    $[3] = isHydrated;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};